import { TimeFrameEnum } from 'Pages/Investigate/QueryBuilder/CrossLogSearch/types';
import React from 'react';
import { DateRangeInputProps, RelativeTimeUnits } from 'pounceV2';
import dayjs, { Dayjs } from 'dayjs';

export const DATE_FORMAT = `MM/DD/YYYY HH:mm`;

export type PresetOption = Partial<DateRangeInputProps['presets'][0]> & {
  id: string;
  value?: number;
  unit?: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
};

export const DAYS_PER_MONTH = 30;

export interface TimeframeValue {
  startTime?: Dayjs | Date | string | null;
  endTime?: Dayjs | Date | string | null;
  relativeNumberVal?: number;
  relativeTimeUnit?: RelativeTimeUnits;
  timeFrame?: TimeFrameEnum;
}

export interface TimeframeSelectionProps {
  value: TimeframeValue;
  onChange: ({ updateType, value }: { updateType: UpdateType; value: TimeframeValue }) => void;
  label?: string;
  inputEndContent?: React.ReactNode;
  customWrapperWidth?: string | number;
  presetOptions?: PresetOption[];
  variant?: 'solid' | 'outline';
}

export enum UpdateType {
  PRESET = 'preset',
  RELATIVE_TIME = 'relativeTime',
  CUSTOM = 'custom',
  OTHER_OPTIONS = 'otherOptions',
  CANCEL = 'cancel',
}

export const PresetOptions: PresetOption[] = [
  {
    label: 'Last 20 mins',
    id: TimeFrameEnum.Last20Minutes as string,
    value: 20,
    unit: 'minutes',
    getValue: now => {
      return [
        dayjs(now).subtract(20, 'minutes').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last hour',
    id: TimeFrameEnum.LastHour as string,
    value: 1,
    unit: 'hours',
    getValue: now => {
      return [
        dayjs(now).subtract(1, 'hour').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 3 hours',
    id: TimeFrameEnum.Last3Hours as string,
    value: 3,
    unit: 'hours',
    getValue: now => {
      return [
        dayjs(now).subtract(3, 'hours').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 6 hours',
    id: TimeFrameEnum.Last6Hours as string,
    value: 6,
    unit: 'hours',
    getValue: now => {
      return [
        dayjs(now).subtract(6, 'hours').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 12 hours',
    id: TimeFrameEnum.Last12Hours as string,
    value: 12,
    unit: 'hours',
    getValue: now => {
      return [
        dayjs(now).subtract(12, 'hours').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 24 hours',
    id: TimeFrameEnum.Last24Hours as string,
    value: 24,
    unit: 'hours',
    getValue: now => {
      return [
        dayjs(now).subtract(1, 'day').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 3 days',
    id: TimeFrameEnum.Last3Days as string,
    value: 3,
    unit: 'days',
    getValue: now => {
      return [
        dayjs(now).subtract(3, 'days').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last week',
    id: TimeFrameEnum.LastWeek as string,
    value: 1,
    unit: 'weeks',
    getValue: now => {
      return [
        dayjs(now).subtract(1, 'week').startOf('minute').toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last month',
    id: TimeFrameEnum.LastMonth as string,
    value: 1,
    unit: 'months',
    getValue: now => {
      return [
        dayjs(now)
          .subtract(1 * DAYS_PER_MONTH, 'days')
          .startOf('minute')
          .toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 3 months',
    id: TimeFrameEnum.Last3Months as string,
    value: 3,
    unit: 'months',
    getValue: now => {
      return [
        dayjs(now)
          .subtract(3 * DAYS_PER_MONTH, 'days')
          .startOf('minute')
          .toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'Last 6 months',
    id: TimeFrameEnum.Last6Months as string,
    value: 6,
    unit: 'months',
    getValue: now => {
      return [
        dayjs(now)
          .subtract(6 * DAYS_PER_MONTH, 'days')
          .startOf('minute')
          .toDate(),
        dayjs(now).endOf('minute').toDate(),
      ];
    },
  },
  {
    label: 'All time',
    id: TimeFrameEnum.AllTime as string,
  },
  {
    label: 'Relative time',
    id: TimeFrameEnum.RelativeTimeCustom as string,
  },
  {
    label: 'Custom range',
    id: TimeFrameEnum.Custom as string,
  },
];
